import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import * as LocationPickerActions from './location-picker.actions';
import {
  animationFrameScheduler,
  catchError,
  distinctUntilChanged,
  first,
  forkJoin,
  map,
  observeOn,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { Router } from '@angular/router';
import { UserSettingService } from '@firebird-web/user-settings-store';
import { LocationPickerFacade } from './location-picker.facade';
import { LocationPickerApiService } from '@firebird-web/location-picker-store';
import { CitiesService } from '@firebird-web/cities-store';
import { initialLocationPickerState } from '../constants/location-picker.constant';
import { LocationPickers } from '@firebird-web/location-picker-store';
import { PermissionsService } from '../../../../acl/src/lib/permissions.service';

@Injectable()
export class LocationPickerEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly userSettingsService: UserSettingService,
    private readonly locationPicker: LocationPickerFacade,
    private readonly locationPickerApiService: LocationPickerApiService,
    private readonly citiesService: CitiesService,
    private readonly permissionsService: PermissionsService
  ) {}

  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationPickerActions.initLocationPicker),
      switchMap(() => this.locationPickerApiService.getPickerData()),
      switchMap((persistedState) => {
        const availableContinents = Object.entries(
          this.permissionsService.getPermissions()['PoolSet']
        )
          .filter(([, value]) => value === true)
          .map(([key]) => {
            if (key.includes('NA')) {
              return 'NA';
            }

            if (key.includes('EUR')) {
              return 'EUR';
            }

            if (key.includes('MEX')) {
              return 'MEX';
            }

            if (key.includes('ASIA')) {
              return 'ASIA';
            }

            return key;
          });

        const defaultState = {
          ...initialLocationPickerState,
          [LocationPickers.CITY]: {
            ...initialLocationPickerState[LocationPickers.CITY],
            continent: availableContinents[0],
          },
        };
        const initState = persistedState ? persistedState : defaultState;

        return forkJoin([
          of(initState),
          this.citiesService.getCitiesList(
            initState[LocationPickers.CITY].continent as string,
            initState[LocationPickers.CITY].region as string
          ),
        ]);
      }),
      map(([persistedState, cities]) =>
        persistedState[LocationPickers.CITY].siteId
          ? persistedState
          : {
              ...persistedState,
              [LocationPickers.CITY]: {
                ...persistedState[LocationPickers.CITY],
                siteId: cities.length > 0 ? cities[0].siteId : null,
              },
            }
      ),
      first(),
      switchMap((persistedState) => [
        LocationPickerActions.initLocationPickerSuccess({ persistedState }),
        LocationPickerActions.initDataForCurrentPage({
          path: this.userSettingsService.getCurrentLocationUrlArray().join('/'),
          queryParams: this.userSettingsService.getCurrentLocationQueryParams(),
        }),
        LocationPickerActions.initCityData(),
      ]),
      catchError((error) =>
        of(LocationPickerActions.initLocationPickerFailure({ error }))
      )
    )
  );

  initCityData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationPickerActions.initCityData),
      switchMap(() => this.locationPicker.getCityData()),
      first(),
      distinctUntilChanged(
        (prev, curr) =>
          prev.continent === curr.continent && prev.region === curr.region
      ),
      map((location) => {
        return LocationPickerActions.saveDataForCity({ location });
      })
    )
  );

  saveLocationPickerRegionData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LocationPickerActions.saveDataForRegion),
        // TODO: navigate with new query parameters is being canceled by another navigate with old parameters, added timeout to fix it, need to find a better solution
        observeOn(animationFrameScheduler),
        tap(({ location, config }) => {
          if (
            this.userSettingsService.getCurrentLocationUrlArray()[1] !==
              'region-view' &&
            !config?.redirect
          ) {
            return;
          }
          this.router.navigate(
            config?.redirect ? [`1-15days/region-view/${config?.path}`] : [],
            {
              queryParamsHandling: 'merge',
              queryParams: {
                ...location,
                ...config?.queryParams,
              },
            }
          );
        })
      ),
    { dispatch: false }
  );

  saveLocationPickerCityData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LocationPickerActions.saveDataForCity),
        // TODO: navigate with new query parameters is being canceled by another navigate with old parameters, added timeout to fix it, need to find a better solution
        observeOn(animationFrameScheduler),
        tap(({ location, config }) => {
          if (
            this.userSettingsService.getCurrentLocationUrlArray()[1] !==
              'city-view' &&
            !config?.redirect
          ) {
            return;
          }
          const queryParams: any = { ...location };
          if (config?.dashboardId !== undefined) {
            queryParams.dashboardId = config.dashboardId;
          }
          this.router.navigate(
            config?.redirect ? [`1-15days/city-view/${config?.path}`] : [],
            {
              queryParamsHandling: 'merge',
              queryParams,
            }
          );
        })
      ),
    { dispatch: false }
  );
}
